/* Enum degerlerinin Turkce karsiliklari.
   Backend enum'lari dize olarak gonderir; arayuz burada insan diline cevirir. */

import type {
  BulkOperationStatus,
  BulkOperationType,
  CollectionRuleField,
  CollectionRuleOperator,
  ContactMessageStatus,
  DiscountAllocationMethod,
  DiscountCombination,
  DiscountCustomerSelection,
  DiscountSelection,
  DiscountTarget,
  DiscountType,
  FinancialStatus,
  FulfillmentStatus,
  GiftCardStatus,
  InventoryMovementType,
  LocationKind,
  OrderCancelReason,
  OrderStatus,
  PaymentMethodType,
  ProductKind,
  ProductStatus,
  RefundStatus,
  ReturnDisposition,
  ReturnReason,
  ReturnStatus,
  ReviewStatus,
  ShipmentTrackingStatus,
  ShippingRateType,
  ShippingSpeed,
  ShippingZoneScope,
} from '../api/types';

type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export interface StatusLabel {
  text: string;
  tone: Tone;
}

const toneClass: Record<Tone, string> = {
  neutral: 'bg-ink-100 text-ink-700',
  info: 'bg-brand-50 text-brand-800',
  success: 'bg-emerald-50 text-emerald-800',
  warning: 'bg-amber-50 text-amber-800',
  danger: 'bg-red-50 text-red-700',
};

export function badgeClass(tone: Tone): string {
  return toneClass[tone];
}

export const orderStatusLabels: Record<OrderStatus, StatusLabel> = {
  Draft: { text: 'Taslak', tone: 'neutral' },
  Open: { text: 'Açık', tone: 'info' },
  Archived: { text: 'Arşivlendi', tone: 'neutral' },
  Cancelled: { text: 'İptal edildi', tone: 'danger' },
};

export const financialStatusLabels: Record<FinancialStatus, StatusLabel> = {
  Pending: { text: 'Ödeme bekliyor', tone: 'warning' },
  Authorized: { text: 'Provizyon alındı', tone: 'info' },
  PartiallyPaid: { text: 'Kısmen ödendi', tone: 'warning' },
  Paid: { text: 'Ödendi', tone: 'success' },
  PartiallyRefunded: { text: 'Kısmen iade', tone: 'warning' },
  Refunded: { text: 'İade edildi', tone: 'neutral' },
  Voided: { text: 'İptal edildi', tone: 'danger' },
  Expired: { text: 'Süresi doldu', tone: 'danger' },
};

export const fulfillmentStatusLabels: Record<FulfillmentStatus, StatusLabel> = {
  Unfulfilled: { text: 'Hazırlanıyor', tone: 'warning' },
  PartiallyFulfilled: { text: 'Kısmen sevk edildi', tone: 'info' },
  Fulfilled: { text: 'Sevk edildi', tone: 'success' },
  Restocked: { text: 'Stoğa geri alındı', tone: 'neutral' },
  Cancelled: { text: 'İptal', tone: 'danger' },
  OnHold: { text: 'Beklemede', tone: 'warning' },
};

export const productStatusLabels: Record<ProductStatus, StatusLabel> = {
  Draft: { text: 'Taslak', tone: 'neutral' },
  Active: { text: 'Yayında', tone: 'success' },
  Archived: { text: 'Arşiv', tone: 'neutral' },
};

export const returnStatusLabels: Record<ReturnStatus, StatusLabel> = {
  Requested: { text: 'Talep edildi', tone: 'warning' },
  Approved: { text: 'Onaylandı', tone: 'info' },
  Rejected: { text: 'Reddedildi', tone: 'danger' },
  InTransit: { text: 'Yolda', tone: 'info' },
  Received: { text: 'Teslim alındı', tone: 'info' },
  Inspecting: { text: 'İnceleniyor', tone: 'info' },
  Completed: { text: 'Tamamlandı', tone: 'success' },
  Cancelled: { text: 'İptal edildi', tone: 'neutral' },
};

export const refundStatusLabels: Record<RefundStatus, StatusLabel> = {
  Pending: { text: 'Ödeme bekliyor', tone: 'warning' },
  Processing: { text: 'İşleniyor', tone: 'info' },
  Completed: { text: 'Tamamlandı', tone: 'success' },
  Failed: { text: 'Başarısız', tone: 'danger' },
  Cancelled: { text: 'İptal edildi', tone: 'neutral' },
};

export const shipmentStatusLabels: Record<ShipmentTrackingStatus, StatusLabel> = {
  Unknown: { text: 'Bilinmiyor', tone: 'neutral' },
  LabelCreated: { text: 'Etiket oluşturuldu', tone: 'neutral' },
  PickedUp: { text: 'Kargoya verildi', tone: 'info' },
  InTransit: { text: 'Yolda', tone: 'info' },
  AtDeliveryBranch: { text: 'Dağıtım şubesinde', tone: 'info' },
  OutForDelivery: { text: 'Dağıtımda', tone: 'info' },
  Delivered: { text: 'Teslim edildi', tone: 'success' },
  AttemptedDelivery: { text: 'Teslim denendi', tone: 'warning' },
  Exception: { text: 'Sorun oluştu', tone: 'danger' },
  ReturnedToSender: { text: 'Göndericiye iade', tone: 'neutral' },
  Cancelled: { text: 'İptal edildi', tone: 'danger' },
};

export const returnReasonLabels: Record<ReturnReason, string> = {
  Damaged: 'Hasarlı geldi',
  Defective: 'Ürün arızalı',
  NotAsDescribed: 'Açıklamaya uymuyor',
  WrongItemSent: 'Yanlış ürün gönderildi',
  SizeTooSmall: 'Beden küçük geldi',
  SizeTooLarge: 'Beden büyük geldi',
  ChangedMind: 'Vazgeçtim',
  ArrivedLate: 'Geç ulaştı',
  MissingParts: 'Eksik parça',
  Other: 'Diğer',
};

export const returnDispositionLabels: Record<ReturnDisposition, string> = {
  Restock: 'Stoğa geri al',
  Damaged: 'Hasarlı (stoğa girmez)',
  Discard: 'İmha et',
  ReturnToSupplier: 'Tedarikçiye iade',
};

export const paymentMethodLabels: Record<PaymentMethodType, string> = {
  CreditCard: 'Kredi kartı',
  DebitCard: 'Banka kartı',
  BankTransfer: 'Havale / EFT',
  CashOnDelivery: 'Kapıda ödeme',
  GiftCard: 'Hediye kartı',
  StoreCredit: 'Mağaza kredisi',
  DigitalWallet: 'Dijital cüzdan',
  BuyNowPayLater: 'Şimdi al sonra öde',
  Installment: 'Taksitli ödeme',
};

export const discountTypeLabels: Record<DiscountType, string> = {
  Percentage: 'Yüzde indirim',
  FixedAmount: 'Tutar indirimi',
  FreeShipping: 'Ücretsiz kargo',
  BuyXGetY: 'X al Y öde',
};

export const bulkStatusLabels: Record<BulkOperationStatus, StatusLabel> = {
  Queued: { text: 'Sırada', tone: 'neutral' },
  Running: { text: 'Çalışıyor', tone: 'info' },
  Completed: { text: 'Tamamlandı', tone: 'success' },
  CompletedWithErrors: { text: 'Hatalarla bitti', tone: 'warning' },
  Failed: { text: 'Başarısız', tone: 'danger' },
  Cancelled: { text: 'İptal edildi', tone: 'neutral' },
};

export const contactStatusLabels: Record<ContactMessageStatus, StatusLabel> = {
  New: { text: 'Yeni', tone: 'info' },
  Read: { text: 'Okundu', tone: 'neutral' },
  Replied: { text: 'Yanıtlandı', tone: 'success' },
  Closed: { text: 'Kapandı', tone: 'neutral' },
  Spam: { text: 'Spam', tone: 'danger' },
};

export const reviewStatusLabels: Record<ReviewStatus, StatusLabel> = {
  Pending: { text: 'Onay bekliyor', tone: 'warning' },
  Approved: { text: 'Yayında', tone: 'success' },
  Rejected: { text: 'Reddedildi', tone: 'danger' },
  Spam: { text: 'Spam', tone: 'danger' },
};

export const discountTargetLabels: Record<DiscountTarget, string> = {
  LineItem: 'Ürün kalemi',
  Shipping: 'Kargo ücreti',
  Order: 'Tüm sipariş',
};

export const discountSelectionLabels: Record<DiscountSelection, string> = {
  All: 'Tüm ürünler',
  EntitledProducts: 'Belirli ürünler',
  EntitledCategories: 'Belirli kategoriler',
  EntitledCollections: 'Belirli koleksiyonlar',
  EntitledVariants: 'Belirli varyantlar',
};

export const discountCustomerSelectionLabels: Record<DiscountCustomerSelection, string> = {
  All: 'Tüm müşteriler',
  SpecificCustomers: 'Belirli müşteriler',
  CustomerGroups: 'Müşteri grupları',
  FirstTimeBuyers: 'İlk kez alışveriş yapanlar',
};

export const discountCombinationLabels: Record<DiscountCombination, string> = {
  None: 'Hiçbiri (tek başına kullanılır)',
  WithProductDiscounts: 'Ürün indirimleriyle birleşebilir',
  WithShippingDiscounts: 'Kargo indirimleriyle birleşebilir',
  WithOrderDiscounts: 'Sipariş indirimleriyle birleşebilir',
  All: 'Tüm indirimlerle birleşebilir',
};

export const discountAllocationMethodLabels: Record<DiscountAllocationMethod, string> = {
  Across: 'Siparişe dağıt',
  Each: 'Her kaleme ayrı uygula',
};

export const productKindLabels: Record<ProductKind, string> = {
  Physical: 'Fiziksel ürün',
  Digital: 'Dijital ürün',
  Service: 'Hizmet',
  GiftCard: 'Hediye kartı',
  Bundle: 'Ürün paketi (Bundle)',
};

export const collectionRuleFieldLabels: Record<CollectionRuleField, string> = {
  Title: 'Ürün başlığı',
  ProductKind: 'Ürün türü',
  Brand: 'Marka',
  Tag: 'Etiket',
  Price: 'Fiyat',
  CompareAtPrice: 'İndirimsiz fiyat',
  Weight: 'Ağırlık',
  InventoryQuantity: 'Stok adedi',
  VariantTitle: 'Varyant başlığı',
  Sku: 'Stok kodu (SKU)',
  Category: 'Kategori',
  CreatedAt: 'Eklenme tarihi',
};

export const collectionRuleOperatorLabels: Record<CollectionRuleOperator, string> = {
  Equals: 'Eşittir',
  NotEquals: 'Eşit değildir',
  GreaterThan: 'Büyüktür',
  LessThan: 'Küçüktür',
  GreaterThanOrEqual: 'Büyük veya eşittir',
  LessThanOrEqual: 'Küçük veya eşittir',
  StartsWith: 'İle başlar',
  EndsWith: 'İle biter',
  Contains: 'İçerir',
  NotContains: 'İçermez',
  IsSet: 'Tanımlı',
  IsNotSet: 'Tanımlı değil',
};

export const orderCancelReasonLabels: Record<OrderCancelReason, string> = {
  CustomerRequest: 'Müşteri talebi',
  Fraud: 'Şüpheli / sahte işlem',
  InventoryUnavailable: 'Stok yetersiz',
  PaymentDeclined: 'Ödeme başarısız',
  Duplicate: 'Mükerrer sipariş',
  Other: 'Diğer',
};

export const locationKindLabels: Record<LocationKind, string> = {
  Warehouse: 'Depo',
  RetailStore: 'Mağaza',
  Dropshipper: 'Tedarikçi (Dropshipper)',
  Virtual: 'Sanal konum',
};

export const shippingRateTypeLabels: Record<ShippingRateType, string> = {
  Flat: 'Sabit ücret',
  PriceBased: 'Tutar bazlı',
  WeightBased: 'Ağırlık bazlı',
  ItemBased: 'Ürün adedi bazlı',
  CarrierCalculated: 'Kargo firması tarafından',
  Free: 'Ücretsiz',
};

export const shippingSpeedLabels: Record<ShippingSpeed, string> = {
  Standard: 'Standart kargo',
  Express: 'Hızlı kargo (Express)',
  SameDay: 'Aynı gün teslimat',
  NextDay: 'Ertesi gün teslimat',
  Economy: 'Ekonomik kargo',
  StorePickup: 'Mağazadan teslim',
};

export const shippingZoneScopeLabels: Record<ShippingZoneScope, string> = {
  Country: 'Ülke',
  Province: 'İl / Eyalet',
  PostalCode: 'Posta kodu',
  RestOfWorld: 'Diğer ülkeler',
};

export const inventoryMovementTypeLabels: Record<InventoryMovementType, string> = {
  Adjustment: 'Düzeltme',
  Sale: 'Satış',
  ReturnRestock: 'İade (stoğa giriş)',
  TransferOut: 'Transfer çıkışı',
  TransferIn: 'Transfer girişi',
  Receipt: 'Mal kabul',
  Damage: 'Hasarlı ürün',
  Loss: 'Kayıp / zayi',
  Reservation: 'Rezervasyon',
  ReservationRelease: 'Rezervasyon iptali',
  Fulfillment: 'Sevkiyat',
  Cancellation: 'Sipariş iptali',
  BulkImport: 'Toplu içe aktarma',
};

export const bulkOperationTypeLabels: Record<BulkOperationType, string> = {
  ProductImport: 'Ürün içe aktarma',
  ProductExport: 'Ürün dışa aktarma',
  ProductBulkUpdate: 'Ürün toplu güncelleme',
  ProductBulkDelete: 'Ürün toplu silme',
  InventoryImport: 'Stok içe aktarma',
  InventoryBulkAdjust: 'Toplu stok düzeltme',
  PriceBulkUpdate: 'Toplu fiyat güncelleme',
  OrderExport: 'Sipariş dışa aktarma',
  OrderBulkFulfill: 'Toplu sevkiyat',
  CustomerImport: 'Müşteri içe aktarma',
  CustomerExport: 'Müşteri dışa aktarma',
  ImageImport: 'Görsel içe aktarma',
};

export const giftCardStatusLabels: Record<GiftCardStatus, string> = {
  Active: 'Aktif',
  PartiallyRedeemed: 'Kısmen kullanıldı',
  Redeemed: 'Tükendi',
  Expired: 'Süresi doldu',
  Disabled: 'Devre dışı',
};

/** Bilinmeyen bir enum degeri gelirse uygulama patlamasin. */
export function labelOf<T extends string>(
  map: Record<string, StatusLabel>,
  value: T | null | undefined,
): StatusLabel {
  if (!value) return { text: '—', tone: 'neutral' };
  return map[value] ?? { text: value, tone: 'neutral' };
}

/** Sozluk disi enum degerleri icin duz metin karsiligi. */
export function textOf<T extends string>(
  map: Record<string, string>,
  value: T | null | undefined,
): string {
  if (!value) return '—';
  return map[value] ?? value;
}
